/**
 * useProvidersList Hook
 *
 * Fetches and manages provider list data for the audit dashboard
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetAdminProvidersQuery } from '../store/adminApi';
import { useGetDashboardProvidersQuery } from '../store/api';
import type { ProviderSummary } from '../types';

interface UseProvidersListParams {
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
  /** Period in days */
  days?: number;
  /** Explicit start date (ISO 8601 format) */
  dateFrom?: string;
  /** Explicit end date (ISO 8601 format) */
  dateTo?: string;
  /** Comma-separated provider IDs to filter by */
  userIds?: string;
}

interface UseProvidersListResult {
  /** List of providers */
  providers: ProviderSummary[];
  /** Total number of providers */
  total: number;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether list is empty */
  isEmpty: boolean;
}

export const useProvidersList = (
  params?: UseProvidersListParams,
): UseProvidersListResult => {
  const filterParams = params
    ? {
        ...(params.days !== undefined && { days: params.days }),
        ...(params.dateFrom && { dateFrom: params.dateFrom }),
        ...(params.dateTo && { dateTo: params.dateTo }),
        ...(params.userIds && { userIds: params.userIds }),
      }
    : {};

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminProvidersQuery(
    params?.organizationId
      ? {
          organizationId: params.organizationId,
          ...filterParams,
        }
      : skipToken,
  );

  const providerQuery = useGetDashboardProvidersQuery(
    !params?.organizationId ? filterParams : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, error, refetch } = params?.organizationId
    ? adminQuery
    : providerQuery;

  const providers = useMemo(() => data?.providers || [], [data]);
  const total = data?.total || 0;
  const isEmpty = !isLoading && providers.length === 0;

  return {
    providers,
    total,
    isLoading,
    error: error ? 'Failed to load providers list' : null,
    refetch,
    isEmpty,
  };
};

export default useProvidersList;

